import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { CalendarIcon, Users, DollarSign, Clock, MapPin, Bed, Bath, ArrowRight, Loader2, AlertCircle, Building2, Hash, ChevronDown, ChevronRight, Lock } from 'lucide-react';
import { format, addDays, differenceInDays } from 'date-fns';
import { ru } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useRooms } from '@/hooks/useRooms';
import { useRoomCategories } from '@/hooks/useRoomCategories';
import { useRoomAvailability, conflictText } from '@/hooks/useRoomAvailability';
import { useOptimizedHotel } from '@/contexts/OptimizedHotelContext';
import { useToast } from '@/hooks/use-toast';
import { usePublicBooking } from '@/hooks/usePublicBooking';
import { supabase } from '@/integrations/supabase/client';
import { logChangeWithContext } from '@/utils/bookingLogger';
import { useRatePlans } from '@/hooks/useRatePlans';
import { useSeasonalRates } from '@/hooks/useSeasonalRates';
import { findOptimalRoom } from '@/utils/roomAutoAssignment';

interface QuickBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRoom?: any;
  selectedDate?: Date;
  selectedCheckOutDate?: Date;
  bookings?: any[];
  onQuickBookingSave?: (roomId: string, checkInDate: string, checkOutDate: string) => Promise<void>;
}

// Мемоизированный компонент карточки категории с данными вместимости
const RoomCategoryCard = React.memo(({ 
  category, 
  loading, 
  onBook,
  onBookAndEdit 
}: { 
  category: any; 
  loading: boolean; 
  onBook: () => void;
  onBookAndEdit?: () => void;
}) => (
  <Card className="hover:shadow-lg transition-all duration-200 cursor-pointer border-2 hover:border-primary/30">
    <CardContent className="p-4">
      <div className="space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="font-semibold text-lg">{category.type}</h3>
            {/* Расширенные данные вместимости */}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <Badge variant="outline" className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {category.placesIncluded || category.capacity} чел.
              </Badge>
              {category.maxPlaces > (category.placesIncluded || category.capacity) && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  макс. {category.maxPlaces}
                </Badge>
              )}
              {category.childPlaces > 0 && (
                <Badge variant="secondary" className="flex items-center gap-1 text-primary">
                  +{category.childPlaces} детей
                </Badge>
              )}
              {category.areaSqm > 0 && (
                <Badge variant="outline" className="text-muted-foreground">
                  {category.areaSqm}м²
                </Badge>
              )}
            </div>
            <div className="mt-2">
              <Badge variant="secondary">
                {category.availableCount} доступно
              </Badge>
            </div>
          </div>
          <div className="text-right ml-4">
            {category.priceSource === 'price_not_available' || category.totalPrice <= 0 ? (
              <>
                <div className="text-2xl font-bold text-muted-foreground">—</div>
                <div className="text-sm text-muted-foreground">price_not_available</div>
              </>
            ) : (
              <>
                <div className="text-2xl font-bold text-primary">
                  ₽{Math.round(category.totalPrice).toLocaleString()}
                </div>
                <div className="text-sm text-muted-foreground">
                  ₽{Math.round(category.minPrice).toLocaleString()} за ночь
                </div>
              </>
            )}
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onBook();
              }}
              disabled={loading}
              className="w-full mt-2"
              size="sm"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Создаю...
                </>
              ) : (
                'Забронировать'
              )}
            </Button>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
));

RoomCategoryCard.displayName = 'RoomCategoryCard';

// Мемоизированный компонент карточки конкретного номера
// 🎯 КРИТИЧНО: pricePerNight должен приходить из сезонной архитектуры, НЕ из room.price_per_night
const SpecificRoomCard = React.memo(({ 
  room, 
  nights,
  pricePerNight, // Цена за ночь из сезонной архитектуры (или fallback из категории)
  totalPrice, // Общая цена за период
  isSelected,
  loading, 
  onSelect,
  onBook,
  onBookAndEdit 
}: { 
  room: any; 
  nights: number;
  pricePerNight: number;
  totalPrice: number;
  isSelected: boolean; 
  loading: boolean; 
  onSelect: () => void;
  onBook: () => void;
  onBookAndEdit?: () => void;
}) => (
  <Card 
    className={cn(
      "hover:shadow-md transition-shadow cursor-pointer",
      isSelected && "ring-2 ring-primary bg-primary/5"
    )}
    onClick={onSelect}
  >
    <CardContent className="p-3">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Hash className="h-4 w-4 text-muted-foreground" />
            <span className="font-semibold">
              {room.room_number || room.name}
            </span>
          </div>
          {isSelected && (
            <Badge variant="default" className="text-xs">
              Выбран
            </Badge>
          )}
        </div>
        
        <div className="text-sm text-muted-foreground space-y-1">
          {room.floor_number && (
            <div className="flex items-center gap-1">
              <Building2 className="h-3 w-3" />
              <span>{room.floor_number} этаж</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Users className="h-3 w-3" />
            <span>до {room.capacity} гостей</span>
          </div>
          <div className="flex items-center gap-1">
            <Bed className="h-3 w-3" />
            <span>{room.bed_type || 'Стандарт'}</span>
          </div>
        </div>
        
        <div className="pt-2 border-t">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div>
                {totalPrice > 0 ? (
                  <>
                    <div className="text-lg font-bold text-primary">
                      ₽{Math.round(totalPrice).toLocaleString()}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      ₽{Math.round(pricePerNight).toLocaleString()} за ночь
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-lg font-bold text-muted-foreground">—</div>
                    <div className="text-xs text-muted-foreground">price_not_available</div>
                  </>
                )}
              </div>
            </div>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onBook();
              }}
              disabled={loading}
              size="sm"
              variant="outline"
              className="w-full"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Забронировать'
              )}
            </Button>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
));

SpecificRoomCard.displayName = 'SpecificRoomCard';

export const QuickBookingModal: React.FC<QuickBookingModalProps> = ({
  isOpen,
  onClose,
  selectedRoom,
  selectedDate,
  selectedCheckOutDate,
  bookings = [],
  onQuickBookingSave
}) => {
  const { rooms, loading: roomsLoading } = useRooms();
  const { categories: dbCategories, loading: categoriesLoading } = useRoomCategories();
  const { currentHotel } = useOptimizedHotel();
  const { toast } = useToast();
  const { createBooking, loading } = usePublicBooking();
  const { ratePlans, getDefaultRatePlan } = useRatePlans();
  
  // 🎯 КРИТИЧНО: Получаем дефолтный тариф для передачи в usePublicRoomRates
  const [selectedRatePlanId, setSelectedRatePlanId] = useState<string | undefined>(undefined);
  
  // Устанавливаем дефолтный тариф при загрузке
  useEffect(() => {
    if (ratePlans.length > 0 && !selectedRatePlanId) {
      const defaultPlan = getDefaultRatePlan();
      if (defaultPlan) {
        setSelectedRatePlanId(defaultPlan.id);
        console.log('✅ QuickBookingModal: Установлен дефолтный тариф:', defaultPlan.name, defaultPlan.id);
      }
    }
  }, [ratePlans, selectedRatePlanId, getDefaultRatePlan]);
  
  // State для дат (объявляем ПЕРЕД использованием в usePublicRoomRates)
  const [checkInDate, setCheckInDate] = useState<Date>(selectedDate || new Date());
  const [checkOutDate, setCheckOutDate] = useState<Date>(
    selectedCheckOutDate || addDays(selectedDate || new Date(), 1)
  );
  
  // 🚀 ОПТИМИЗАЦИЯ: useSeasonalRates (1 RPC) вместо usePublicRoomRates (12+ RPCs)
  const { rates: seasonalRatesData, loading: ratesLoading } = useSeasonalRates(
    checkInDate,
    checkOutDate,
    selectedRatePlanId
  );
  
  // Преобразуем формат для совместимости
  const roomRates = useMemo(() => {
    const result: Record<string, Record<string, { final_price: number }>> = {};
    Object.entries(seasonalRatesData).forEach(([categoryId, dateRates]) => {
      result[categoryId] = {};
      Object.entries(dateRates).forEach(([date, rateData]) => {
        result[categoryId][date] = { final_price: rateData.price };
      });
    });
    return result;
  }, [seasonalRatesData]);
  
  // 🎯 AK-47: занятость номеров считает сервер (см. useRoomAvailability ниже)

  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: selectedDate || new Date(),
    to: selectedCheckOutDate || addDays(selectedDate || new Date(), 1)
  });
  const [checkInTime, setCheckInTime] = useState('14:00');
  const [checkOutTime, setCheckOutTime] = useState('12:00');
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [guestMiddleName, setGuestMiddleName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [comment, setComment] = useState(''); // Комментарий к бронированию
  const [userChangedDates, setUserChangedDates] = useState(false);
  const [showAllRooms, setShowAllRooms] = useState(false);
  const [blockReason, setBlockReason] = useState<string>(''); // Причина блокировки
  const [isBlockMode, setIsBlockMode] = useState(false); // Режим блокировки
  
  // 🎯 Режим отображения модала: compact (узкий) или full (широкий)
  // По умолчанию компактный режим на всех страницах
  const [viewMode, setViewMode] = useState<'compact' | 'full'>('compact');
  
  // New states for room selection
  const [selectionMode, setSelectionMode] = useState<'category' | 'specific'>('category');
  const [selectedRoomCategory, setSelectedRoomCategory] = useState<string>('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null); // 🎯 ID категории для надёжного пересчёта
  const [selectedSpecificRoom, setSelectedSpecificRoom] = useState<any>(null);
  const [isAutoSelectMode, setIsAutoSelectMode] = useState(true); // 🎯 Флаг авто-выбора номера
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  
  // 📊 Состояния для тарификации по дням
  const [dailyRates, setDailyRates] = useState<{ date: string; price: number; seasonName?: string }[]>([]);
  const [isLoadingDailyRates, setIsLoadingDailyRates] = useState(false);
  const [showDailyRates, setShowDailyRates] = useState(false);
  
  // 🚀 ОПТИМИЗАЦИЯ: Мемоизированный totalPrice из dailyRates (единственный источник правды)
  const totalPrice = useMemo(() => {
    return dailyRates.reduce((sum, r) => sum + r.price, 0);
  }, [dailyRates]);

  // Sync dates when selectedDate changes or modal opens
  useEffect(() => {
    if (selectedDate && isOpen && !userChangedDates) {
      const from = selectedDate;
      const to = selectedCheckOutDate || addDays(selectedDate, 1);
      setCheckInDate(from);
      setCheckOutDate(to);
      setDateRange({ from, to });
    }
  }, [selectedDate, selectedCheckOutDate, isOpen, userChangedDates]);

  // 🎯 При открытии модала всегда компактный режим (узкий)
  useEffect(() => {
    if (isOpen) {
      setViewMode('compact');
      console.log('📋 ViewMode: compact (default for all pages)');
    }
  }, [isOpen]);

  // Auto-select category AND room when selectedRoom is provided
  useEffect(() => {
    if (selectedRoom && isOpen && dbCategories.length > 0) {
      // 🎯 ИСПРАВЛЕНИЕ: Поддержка обоих форматов - room_category_id И categoryId
      const categoryId = selectedRoom.room_category_id || selectedRoom.categoryId;
      
      // Try to find category by ID first
      let category = dbCategories.find(cat => cat.id === categoryId);
      
      // If not found by ID, try to find by name matching room_type
      if (!category && selectedRoom.room_type) {
        category = dbCategories.find(cat => 
          cat.name.toLowerCase() === selectedRoom.room_type.toLowerCase()
        );
      }
      
      const categoryName = category?.name || selectedRoom.room_type;
      if (categoryName) {
        console.log('📋 Auto-selecting category:', categoryName, 'categoryId:', category?.id || categoryId);
        setSelectedRoomCategory(categoryName);
        setSelectedCategoryId(category?.id || categoryId || null); // 🎯 Устанавливаем ID категории при инициализации
        setSelectionMode('category');
        
        // 🎯 КРИТИЧНО: Устанавливаем конкретный выбранный номер
        // Находим полные данные номера из загруженного списка
        const fullRoomData = rooms.find(r => r.id === selectedRoom.id) || selectedRoom;
        setSelectedSpecificRoom(fullRoomData);
        setIsAutoSelectMode(false); // 🎯 Выключаем авто-режим когда номер выбран при открытии
        console.log('✅ Auto-selected specific room:', fullRoomData.room_number || fullRoomData.id);
      }
    }
  }, [selectedRoom, isOpen, dbCategories, rooms]);


  // Сброс данных гостя и параметров при закрытии модала
  useEffect(() => {
    if (!isOpen) {
      setGuestFirstName('');
      setGuestLastName('');
      setGuestMiddleName('');
      setGuestEmail('');
      setGuestPhone('');
      setComment('');
      setAdults(1);
      setChildren(0);
      setUserChangedDates(false);
      setShowAllRooms(false);
      setViewMode('compact'); // 🎯 Всегда compact по умолчанию
      setSelectionMode('category');
      setSelectedRoomCategory('');
      setSelectedSpecificRoom(null);
      setIsAutoSelectMode(true); // 🎯 Сброс в авто-режим
      setExpandedCategories(new Set());
      setBlockReason('');
      setIsBlockMode(false);
    }
  }, [isOpen]);

  // Занятость номеров — серверный вердикт (та же функция, что и триггер защиты от двойного заселения)
  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setUserChangedDates(false);
      setShowAllRooms(false);
      setSelectionMode('category');
      setSelectedRoomCategory('');
      setSelectedCategoryId(null); // 🎯 Сброс ID категории
      setSelectedSpecificRoom(null);
      setIsAutoSelectMode(true); // 🎯 Сброс в авто-режим
      setDailyRates([]);
      setShowDailyRates(false);
    }
  }, [isOpen]);

  // 📊 Загрузка дневных тарифов для таблицы тарификации (оба режима)
  useEffect(() => {
    const loadDailyRates = async () => {
      if (!currentHotel?.id || !checkInDate || !checkOutDate) {
        return;
      }

      // 🎯 Определяем категорию - приоритет ID из стейта
      let categoryId: string | null = selectedCategoryId;
      
      // 1. Из выбранного конкретного номера (стейт)
      if (!categoryId && selectedSpecificRoom?.room_category_id) {
        categoryId = selectedSpecificRoom.room_category_id;
      }
      
      // 2. Из пропса selectedRoom (при клике на ячейку)
      if (!categoryId && selectedRoom?.room_category_id) {
        categoryId = selectedRoom.room_category_id;
      }
      
      // 3. Ищем номер в массиве rooms по ID из selectedRoom
      if (!categoryId && selectedRoom?.id && rooms.length > 0) {
        const foundRoom = rooms.find(r => r.id === selectedRoom.id);
        if (foundRoom?.room_category_id) {
          categoryId = foundRoom.room_category_id;
        }
      }
      
      // 4. По имени категории из стейта (fallback)
      if (!categoryId && selectedRoomCategory && dbCategories.length > 0) {
        const cat = dbCategories.find(c => c.name === selectedRoomCategory);
        categoryId = cat?.id || null;
      }
      
      // 5. По room_type из selectedRoom
      if (!categoryId && selectedRoom?.room_type && dbCategories.length > 0) {
        const cat = dbCategories.find(c => 
          c.name.toLowerCase() === selectedRoom.room_type.toLowerCase()
        );
        categoryId = cat?.id || null;
      }

      if (!categoryId) {
        console.log('📊 DailyRates: categoryId не найден', { selectedCategoryId, selectedSpecificRoom, selectedRoom, selectedRoomCategory });
        setDailyRates([]);
        return;
      }
      
      console.log('📊 DailyRates: загружаем для categoryId:', categoryId);

      setIsLoadingDailyRates(true);
      try {
        // Загружаем сезоны для отображения названий
        const { data: seasonsData } = await supabase
          .from('seasons')
          .select('id, name, start_date, end_date')
          .eq('hotel_id', currentHotel.id);

        // 🚀 ОПТИМИЗАЦИЯ: 1 batch-вызов get_rates_matrix вместо N последовательных get_rate
        // Исключаем день выезда (он не оплачивается)
        const endDateForRpc = new Date(checkOutDate);
        endDateForRpc.setDate(endDateForRpc.getDate() - 1);
        
        const { data: matrixData, error: matrixError } = await supabase.rpc('get_rates_matrix', {
          p_hotel_id: currentHotel.id,
          p_start_date: format(checkInDate, 'yyyy-MM-dd'),
          p_end_date: format(endDateForRpc, 'yyyy-MM-dd'),
          p_rate_plan_id: selectedRatePlanId || null
        });

        if (matrixError) {
          console.error('Ошибка загрузки матрицы цен:', matrixError);
          setDailyRates([]);
          return;
        }

        // Фильтруем по нашей категории
        const categoryRates = (matrixData || []).filter(
          (r: { category_id: string }) => r.category_id === categoryId
        );

        // Маппим в формат dailyRates с названиями сезонов
        const rates: typeof dailyRates = categoryRates.map((rate: { rate_date: string; price: number }) => {
          let seasonName = 'Не сезон';
          if (seasonsData) {
            const matchingSeason = seasonsData.find(s => {
              const sStart = new Date(s.start_date);
              const sEnd = new Date(s.end_date);
              const rateDate = new Date(rate.rate_date);
              return rateDate >= sStart && rateDate <= sEnd;
            });
            if (matchingSeason) seasonName = matchingSeason.name;
          }
          
          return {
            date: rate.rate_date,
            price: rate.price || 0,
            seasonName
          };
        });

        // Сортируем по дате для правильного отображения
        rates.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        setDailyRates(rates);
        console.log('✅ DailyRates загружены через get_rates_matrix:', rates.length, 'ночей, сумма:', rates.reduce((s, r) => s + r.price, 0));
      } catch (error) {
        console.error('Ошибка загрузки дневных тарифов:', error);
        setDailyRates([]);
      } finally {
        setIsLoadingDailyRates(false);
      }
    };

    loadDailyRates();
  }, [currentHotel?.id, checkInDate, checkOutDate, selectedCategoryId, selectedSpecificRoom, selectedRoom, selectedRoomCategory, dbCategories, selectedRatePlanId, rooms]);

  // 🎤 Listen for voice command to open modal with prefilled data
  useEffect(() => {
    const handleVoiceBooking = (event: CustomEvent<{
      category?: string;
      categoryId?: string;
      ratePlanId?: string;
      checkIn?: string;
      checkOut?: string;
      adults?: number;
      children?: number;
      totalAmount?: number;
      guestName?: string;
      guestPhone?: string;
      guestEmail?: string;
    }>) => {
      const detail = event.detail || {}; // 🎯 Защита от undefined
      console.log('🎤 Voice booking event received:', detail);
      
      // Set dates if provided
      if (detail.checkIn) {
        const checkIn = new Date(detail.checkIn);
        setCheckInDate(checkIn);
        setDateRange(prev => ({ ...prev, from: checkIn }));
      }
      if (detail.checkOut) {
        const checkOut = new Date(detail.checkOut);
        setCheckOutDate(checkOut);
        setDateRange(prev => ({ ...prev, to: checkOut }));
      }
      
      // Set guest count
      if (detail.adults) setAdults(detail.adults);
      if (detail.children) setChildren(detail.children);
      
      // Set guest info
      if (detail.guestName) {
        const nameParts = detail.guestName.trim().split(' ');
        if (nameParts.length >= 2) {
          setGuestLastName(nameParts[0]);
          setGuestFirstName(nameParts[1]);
          if (nameParts[2]) setGuestMiddleName(nameParts[2]);
        } else {
          setGuestFirstName(detail.guestName);
        }
      }
      if (detail.guestPhone) setGuestPhone(detail.guestPhone);
      if (detail.guestEmail) setGuestEmail(detail.guestEmail);
      
      // Set comment if provided
      if (detail.totalAmount) {
        setComment(`Голосовое бронирование: ₽${detail.totalAmount}`);
      }
      
      // Set category
      if (detail.category && dbCategories.length > 0) {
        // Find category by name
        const category = dbCategories.find(
          c => c.name.toLowerCase().includes(detail.category!.toLowerCase())
        );
        if (category) {
          setSelectedRoomCategory(category.name);
          setSelectionMode('category');
        }
      }
      
      if (detail.checkIn || detail.checkOut) {
        setUserChangedDates(true);
      }
    };

    window.addEventListener('open-quick-booking', handleVoiceBooking as EventListener);
    return () => {
      window.removeEventListener('open-quick-booking', handleVoiceBooking as EventListener);
    };
  }, [dbCategories]);

  const nights = useMemo(() => differenceInDays(checkOutDate, checkInDate), [checkOutDate, checkInDate]);

  // 🎯 AK-47: серверная проверка занятости (даты, время, буфер уборки — как в триггере)
  const {
    byRoomId: availabilityByRoom,
    isChecking: isCheckingAvailability,
    isFailed: availabilityFailed,
    isInvalidRange: availabilityInvalidRange,
  } = useRoomAvailability({
    hotelId: currentHotel?.id,
    categoryId: null,
    checkIn: checkInDate ? format(checkInDate, 'yyyy-MM-dd') : null,
    checkOut: checkOutDate ? format(checkOutDate, 'yyyy-MM-dd') : null,
    checkInTime,
    checkOutTime,
    excludeBookingId: null,
    enabled: isOpen,
  });

  // Room availability check function - проверяет реальные бронирования из booking_core
  // 🎯 ИСПРАВЛЕНО: используем данные категории для проверки вместимости
  const isRoomAvailable = useCallback((room: any, startDate: Date, endDate: Date, strict: boolean = true) => {
    if (!room) return false;
    
    if (strict) {
      if (room.is_available === false) return false;
      if (room.is_published === false) return false;
      
      // Получаем категорию для проверки расширенной вместимости
      const category = dbCategories.find(c => c.id === room.room_category_id);
      
      // 🎯 Приоритет: max_places > base_capacity > room.capacity
      const maxCapacity = category?.max_places || category?.base_capacity || room.capacity || 0;
      const childPlaces = category?.child_places || 0;
      
      // Взрослые не могут превышать max_places
      if (adults > maxCapacity) return false;
      
      // Общее кол-во гостей = взрослые + дети
      // Дети могут использовать child_places сверх max_places
      const totalGuests = adults + children;
      const totalCapacity = maxCapacity + childPlaces;
      
      if (totalGuests > totalCapacity) return false;
    }
    
    // Занятость — только по ответу сервера. Нет ответа → не утверждаем «свободен».
    const row = availabilityByRoom[room.id];
    return row ? row.is_available : false;
  }, [adults, children, availabilityByRoom, dbCategories]);

  // Get available rooms - оптимизировано
  // 🎯 ИСПРАВЛЕНО: используем данные категории для фильтрации вместимости
  const availableRooms = useMemo(() => {
    if (rooms.length === 0) return [];
    
    // Функция проверки вместимости с учетом категории
    const checkRoomCapacity = (room: any) => {
      const category = dbCategories.find(c => c.id === room.room_category_id);
      const maxCapacity = category?.max_places || category?.base_capacity || room.capacity || 0;
      const childPlaces = category?.child_places || 0;
      
      // Взрослые не могут превышать max_places
      if (adults > maxCapacity) return false;
      
      // Общее кол-во может включать детей на доп местах
      const totalGuests = adults + children;
      const totalCapacity = maxCapacity + childPlaces;
      
      return totalGuests <= totalCapacity;
    };
    
    let filteredRooms;
    
    if (showAllRooms) {
      // При "показать все" тоже учитываем вместимость
      filteredRooms = rooms.filter(checkRoomCapacity);
    } else {
      filteredRooms = rooms.filter(room => {
        // Проверяем вместимость с учетом категории
        if (!checkRoomCapacity(room)) return false;
        
        // Проверяем доступность
        return isRoomAvailable(room, checkInDate, checkOutDate, true);
      });
      
      if (filteredRooms.length < 3) {
        const looselyFiltered = rooms.filter(room => {
          if (!checkRoomCapacity(room)) return false;
          return isRoomAvailable(room, checkInDate, checkOutDate, false);
        });
        
        if (looselyFiltered.length > filteredRooms.length) {
          filteredRooms = looselyFiltered;
        }
      }
      
      if (filteredRooms.length === 0) {
        filteredRooms = rooms.filter(room => {
          if (!checkRoomCapacity(room)) return false;
          return room.is_available !== false && room.is_published !== false;
        });
      }
    }

    // Сортировка НЕ использует room.price_per_night - только реальную доступность
    return filteredRooms.sort((a, b) => {
      if (selectedRoom && a.id === selectedRoom.id) return -1;
      if (selectedRoom && b.id === selectedRoom.id) return 1;
      // Сортируем по номеру комнаты, НЕ по цене (цена должна браться из сезонной архитектуры)
      const aNum = parseInt(a.room_number || '0');
      const bNum = parseInt(b.room_number || '0');
      return aNum - bNum;
    });
  }, [rooms, checkInDate, checkOutDate, selectedRoom, isRoomAvailable, showAllRooms, adults, children, dbCategories]);

  // Group rooms by category using real categories from database
  // 🎯 КРИТИЧНО: Используем цены из сезонной архитектуры, а НЕ из room.price_per_night!
  const roomCategories = useMemo(() => {
    const grouped = availableRooms.reduce((acc, room) => {
      // Find real category from database
      const category = dbCategories.find(cat => cat.id === room.room_category_id);
      const key = category?.name || room.room_type || 'Без категории';
      const categoryId = category?.id || null;
      
      // 🎯 КРИТИЧНО: Вычисляем цену ТОЛЬКО из сезонной архитектуры (room.price_per_night УДАЛЁН!)
      let pricePerNight = 0;
      let priceSource: 'room_rates' | 'price_not_available' = 'price_not_available';
      
      if (categoryId && roomRates[categoryId]) {
        // 🎯 ПРИОРИТЕТ 1: Берём среднюю цену за период из сезонной архитектуры
        const categoryRates = Object.values(roomRates[categoryId]) as { final_price: number }[];
        if (categoryRates.length > 0) {
          pricePerNight = categoryRates.reduce((sum, r) => sum + r.final_price, 0) / categoryRates.length;
          priceSource = 'room_rates';
          console.log(`💰 Категория ${key}: цена из room_rates = ${pricePerNight} (${categoryRates.length} дней)`);
        }
      }
      // 🚫 ЗАПРЕЩЕНО: base_price_per_night fallback
      if (pricePerNight === 0) {
        console.warn(`⚠️ QuickBookingModal: price_not_available for category ${key}`);
      }
      
      if (!acc[key]) {
        acc[key] = {
          type: key,
          categoryId: categoryId,
          rooms: [],
          minPrice: pricePerNight,
          maxPrice: pricePerNight,
          totalPrice: 0,
          amenities: room.amenities || [],
          capacity: room.capacity || 0,
          // 🎯 Расширенные данные вместимости из категории
          placesIncluded: category?.places_included || category?.base_capacity || room.capacity || 0,
          maxPlaces: category?.max_places || category?.base_capacity || room.capacity || 0,
          childPlaces: category?.child_places || 0,
          areaSqm: category?.area_sqm || 0,
          bedrooms: category?.bedrooms || 1,
          bathrooms: category?.bathrooms || 1,
          bedCount: category?.bed_count || 1,
          bedType: category?.bed_type || '',
          isSelectedCategory: false,
          description: category?.description || '',
          priceSource: categoryId && roomRates[categoryId] && Object.keys(roomRates[categoryId]).length > 0 
            ? 'room_rates'
            : 'price_not_available'
        };
      }
      
      acc[key].rooms.push(room);
      // Не обновляем minPrice/maxPrice от room.price_per_night - используем только тариф!
      acc[key].capacity = Math.max(acc[key].capacity, room.capacity || 0);
      
      // Check if this category contains the selected room
      if (selectedRoom && room.id === selectedRoom.id) {
        acc[key].isSelectedCategory = true;
      }
      
      return acc;
    }, {} as Record<string, any>);

    Object.values(grouped).forEach((group: any) => {
      // 🎯 Рассчитываем TOTAL за период используя реальные цены по дням
      if (group.categoryId && roomRates[group.categoryId]) {
        const categoryRates = Object.values(roomRates[group.categoryId]) as { final_price: number }[];
        group.totalPrice = categoryRates.reduce((sum, r) => sum + r.final_price, 0);
        console.log(`💰 Категория ${group.type}: ИТОГО из room_rates = ${group.totalPrice}`);
      } else {
        group.totalPrice = 0;
        group.priceSource = 'price_not_available';
      }
      
      // Считаем только реально доступные номера, проверяя бронирования
      group.availableCount = group.rooms.filter((room: any) => 
        isRoomAvailable(room, checkInDate, checkOutDate, false)
      ).length;
    });

    const sorted = Object.values(grouped).sort((a: any, b: any) => {
      // Prioritize selected room's category
      if (a.isSelectedCategory && !b.isSelectedCategory) return -1;
      if (!a.isSelectedCategory && b.isSelectedCategory) return 1;
      return a.minPrice - b.minPrice;
    });

    return sorted;
  }, [availableRooms, nights, selectedRoom, dbCategories, checkInDate, checkOutDate, isRoomAvailable, roomRates]);

  // 🎯 КРИТИЧНО: Хелпер для получения цены номера из room_rates
  const getRoomPricing = useCallback((room: any): { pricePerNight: number; totalPrice: number; source: string } => {
    const categoryId = room.room_category_id;
    // 🚫 ЗАПРЕЩЕНО: base_price_per_night fallback
    
    // 1. Приоритет: цены из room_rates для категории
    if (categoryId && roomRates[categoryId] && Object.keys(roomRates[categoryId]).length > 0) {
      const categoryRatesData = Object.values(roomRates[categoryId]) as { final_price: number }[];
      const totalPrice = categoryRatesData.reduce((sum, r) => sum + r.final_price, 0);
      const pricePerNight = totalPrice / categoryRatesData.length;
      console.log(`💰 Номер ${room.room_number}: цена из room_rates = ${pricePerNight}, ИТОГО = ${totalPrice}`);
      return { pricePerNight, totalPrice, source: 'room_rates' };
    }
    
    // 2. НЕТ ТАРИФОВ - возвращаем 0, требуется создать тарифы!
    // ⚠️ room.price_per_night ПОЛНОСТЬЮ УДАЛЁН из логики ценообразования
    console.warn(`⚠️ Номер ${room.room_number}: НЕТ ТАРИФОВ! Создайте тарифы в разделе "Номера и тарифы"`);
    return { pricePerNight: 0, totalPrice: 0, source: 'price_not_available' };
  }, [roomRates, dbCategories, nights]);
  
  // Auto-expand selected category
  useEffect(() => {
    if (selectionMode === 'specific' && selectedRoom) {
      const selectedCategory = roomCategories.find((cat: any) => cat.isSelectedCategory) as any;
      if (selectedCategory) {
        setExpandedCategories(new Set([selectedCategory.type]));
      }
    }
  }, [selectionMode, selectedRoom, roomCategories]);

  // Get specific rooms for selected category
  const specificRoomsForCategory = useMemo(() => {
    if (selectionMode !== 'specific' || !selectedRoomCategory) return [];
    
    return availableRooms
      .filter(room => room.room_type === selectedRoomCategory)
      .sort((a, b) => {
        const aNum = parseInt(a.room_number || a.name || '0');
        const bNum = parseInt(b.room_number || b.name || '0');
        return aNum - bNum;
      });
  }, [availableRooms, selectedRoomCategory, selectionMode]);

  // Мемоизированный обработчик бронирования
  const handleBooking = useCallback(async (room?: any) => {
    // Проверяем обязательные поля
    if (!guestPhone || !guestFirstName) {
      toast({
        title: "Ошибка",
        description: "Заполните обязательные поля (телефон и имя)",
        variant: "destructive"
      });
      return;
    }

    if (!currentHotel) {
      toast({
        title: "Ошибка",
        description: "Отель не выбран",
        variant: "destructive"
      });
      return;
    }

    if (loading) return;

    // Determine which room to book
    // 🎯 FIX: Приоритет - переданный room из UI, затем selectedSpecificRoom, затем auto-assign
    let roomToBook = room || selectedSpecificRoom;
    
    if (!roomToBook && selectionMode === 'category' && selectedRoomCategory) {
      // 🎯 Room Auto-Assignment: находим оптимальный номер в категории
      const category = dbCategories.find(c => c.name === selectedRoomCategory);
      if (category && currentHotel) {
        console.log('🎯 Room Auto-Assignment: Ищем оптимальный номер в категории', selectedRoomCategory);
        
        const assignmentResult = await findOptimalRoom(
          currentHotel.id,
          category.id,
          format(checkInDate, 'yyyy-MM-dd'),
          format(checkOutDate, 'yyyy-MM-dd'),
          adults + children
        );
        
        if (assignmentResult.room) {
          roomToBook = availableRooms.find(r => r.id === assignmentResult.room!.id) || {
            id: assignmentResult.room.id,
            name: assignmentResult.room.name,
            room_number: assignmentResult.room.room_number,
            room_type: selectedRoomCategory,
            room_category_id: category.id,
            capacity: assignmentResult.room.capacity
          };
          console.log('✅ Room Auto-Assignment: Выбран номер', roomToBook.name, 'Score:', assignmentResult.score);
          
          toast({
            title: "Авто-выбор номера",
            description: `Оптимальный номер: ${roomToBook.room_number || roomToBook.name}`,
          });
        } else {
          console.warn('⚠️ Room Auto-Assignment: Не найден доступный номер:', assignmentResult.reason);
          // AK-47 fallback: только номера, которые сервер подтвердил свободными
          roomToBook = availableRooms.find(
            r => r.room_type === selectedRoomCategory && availabilityByRoom[r.id]?.is_available === true
          );
        }
      } else {
        // AK-47 fallback: только номера, которые сервер подтвердил свободными
        roomToBook = availableRooms.find(
          r => r.room_type === selectedRoomCategory && availabilityByRoom[r.id]?.is_available === true
        );
      }
    }

    if (!roomToBook) {
      toast({
        title: "Ошибка",
        description: "Нет свободных номеров на выбранные даты (проверено сервером с учётом буфера уборки)",
        variant: "destructive"
      });
      return;
    }

    // 🎯 Используем selectedRatePlanId или fallback на дефолтный тариф
    const ratePlanId = selectedRatePlanId || getDefaultRatePlan()?.id;
    
    if (!ratePlanId) {
      toast({
        title: "Ошибка",
        description: "Не найден тарифный план. Создайте тариф в разделе 'Тарифы'.",
        variant: "destructive"
      });
      return;
    }
    
    // 🚀 ОПТИМИЗАЦИЯ: Используем готовый totalPrice из dailyRates (уже рассчитан через get_rates_matrix)
    // Это устраняет дублирующий вызов recalculateBookingFinancials с N последовательными get_rate
    let finalTotalAmount = totalPrice;
    
    // Валидация: dailyRates должны быть загружены
    if (finalTotalAmount <= 0 || dailyRates.length === 0) {
      toast({
        title: "Ошибка",
        description: "Цены ещё загружаются. Дождитесь загрузки тарифов.",
        variant: "destructive"
      });
      return;
    }
    
    console.log('💰 Используем готовую цену из dailyRates:', finalTotalAmount, '(', dailyRates.length, 'ночей)');
      
    if (finalTotalAmount <= 0) {
      toast({
        title: "Ошибка",
        description: "Не удалось рассчитать стоимость. Создайте тарифы в /admin/rooms",
        variant: "destructive"
      });
      return;
    }

    try {
      // Обычный режим - создание брони
      const cleanGuestEmail = guestEmail.trim();

      const bookingData = {
        hotel_id: currentHotel.id,
        room_type: roomToBook.room_type,
        room_id: roomToBook.id,
        check_in_date: format(checkInDate, 'yyyy-MM-dd'),
        check_out_date: format(checkOutDate, 'yyyy-MM-dd'),
        check_in_time: checkInTime,
        check_out_time: checkOutTime,
        status: 'confirmed',
        number_of_guests: adults + children,
        adult_count: adults,
        children_count: children,
        infants_count: 0,
        total_amount: finalTotalAmount,
        special_requests: comment || '',
        guest_name: `${guestFirstName} ${guestLastName || ''}`.trim() || 'Гость',
        first_name: guestFirstName || 'Гость',
        last_name: guestLastName || '',
        middle_name: guestMiddleName || '',
        guest_email: cleanGuestEmail,
        guest_phone: guestPhone,
        rate_plan_id: ratePlanId,
        source: 'manual',
        email_required: false,
        price_locked: false,
        manual_total_amount: null,
      } as any;

      const result = await createBooking(bookingData);
      
      if (result.success && result.booking) {
        // Логируем создание брони с контекстными данными
        try {
          // Находим конкретный номер для логирования
          const roomNumber = roomToBook.room_number || roomToBook.name || '';
          
          await logChangeWithContext(
            result.booking.id,
            'status',
            null,
            'pending',
            {
              check_in_date: format(checkInDate, 'yyyy-MM-dd'),
              check_out_date: format(checkOutDate, 'yyyy-MM-dd'),
              total_amount: finalTotalAmount,
              room_type: roomToBook.room_type,
              room_number: roomNumber
            },
            'Создание новой брони через Quick Booking'
          );
        } catch (error) {
          console.error('Ошибка логирования создания брони:', error);
        }
        
        // Сначала закрываем QuickBookingModal
        onClose();
        
        // Очищаем форму
        setGuestFirstName('');
        setGuestLastName('');
        setGuestMiddleName('');
        setGuestEmail('');
        setGuestPhone('');
        setComment('');
        
        toast({
          title: "Бронирование создано",
          description: `Номер ${roomToBook.name} успешно забронирован. Номер брони: ${result.booking_reference}`,
        });

        // Небольшая задержка перед открытием SuperAdvancedModal (200мс для плавности)
        setTimeout(async () => {
          // Notify parent to refresh the grid AND open SuperAdvancedModal
          if (onQuickBookingSave) {
            await onQuickBookingSave(
              roomToBook.id, 
              format(checkInDate, 'yyyy-MM-dd'),
              format(checkOutDate, 'yyyy-MM-dd')
            );
          }
        }, 200);
        setAdults(1);
        setChildren(0);
        setSelectedSpecificRoom(null);
        setSelectedRoomCategory('');
      } else {
        throw new Error(result.error || 'Не удалось создать бронирование');
      }
    } catch (error: any) {
      toast({
        title: "Ошибка создания бронирования",
        description: error.message || "Не удалось создать бронирование",
        variant: "destructive"
      });
    }
  }, [guestPhone, guestFirstName, currentHotel, loading, selectionMode, selectedSpecificRoom, 
      selectedRoomCategory, availableRooms, checkInDate, checkOutDate, adults, children, 
      nights, guestLastName, guestMiddleName, guestEmail, comment, 
      createBooking, toast, onClose, onQuickBookingSave]);

  // 🔒 Обработчик блокировки номера
  const handleBlock = useCallback(async () => {
    if (!currentHotel) {
      toast({ title: "Ошибка", description: "Отель не выбран", variant: "destructive" });
      return;
    }
    if (!blockReason) {
      toast({ title: "Ошибка", description: "Выберите причину блокировки", variant: "destructive" });
      return;
    }

    // Determine room
    let roomToBlock = selectedSpecificRoom;
    if (!roomToBlock && selectionMode === 'category' && selectedRoomCategory) {
      const category = dbCategories.find(c => c.name === selectedRoomCategory);
      if (category) {
        roomToBlock = availableRooms.find(r => r.room_category_id === category.id);
      }
    }
    if (!roomToBlock) {
      toast({ title: "Ошибка", description: "Выберите номер для блокировки", variant: "destructive" });
      return;
    }

    try {
      const { data: blockData, error: blockError } = await supabase
        .from('booking_core')
        .insert({
          hotel_id: currentHotel.id,
          room_type: roomToBlock.room_type || roomToBlock.name,
          room_id: roomToBlock.id,
          check_in_date: format(checkInDate, 'yyyy-MM-dd'),
          check_out_date: format(checkOutDate, 'yyyy-MM-dd'),
          status: 'blocked',
          is_block: true,
          block_reason: blockReason,
          number_of_guests: 0,
          total_amount: 0,
          special_requests: `БЛОК: ${blockReason}`,
        } as any)
        .select('id')
        .single();

      if (blockError) throw blockError;

      toast({
        title: "🔒 Номер заблокирован",
        description: `${roomToBlock.room_number || roomToBlock.name}: ${blockReason}`,
      });

      onClose();
      setBlockReason('');
      setIsBlockMode(false);

      if (onQuickBookingSave) {
        await onQuickBookingSave(
          roomToBlock.id,
          format(checkInDate, 'yyyy-MM-dd'),
          format(checkOutDate, 'yyyy-MM-dd')
        );
      }
    } catch (error: any) {
      toast({
        title: "Ошибка блокировки",
        description: error.message || "Не удалось заблокировать номер",
        variant: "destructive"
      });
    }
  }, [currentHotel, blockReason, selectedSpecificRoom, selectionMode, selectedRoomCategory,
      dbCategories, availableRooms, checkInDate, checkOutDate, toast, onClose, onQuickBookingSave]);

  // Обработчик выбора диапазона дат (один клик выбирает заезд и выезд)
  const handleDateRangeChange = useCallback((range: { from: Date | undefined; to: Date | undefined } | undefined) => {
    if (range?.from) {
      setCheckInDate(range.from);
      setDateRange({ from: range.from, to: range.to });
      setUserChangedDates(true);
      
      if (range.to) {
        setCheckOutDate(range.to);
      } else {
        // Если выбрана только одна дата, выезд = заезд + 1 день
        setCheckOutDate(addDays(range.from, 1));
      }
    }
  }, []);

  const handleSelectionModeChange = useCallback((mode: 'category' | 'specific') => {
    setSelectionMode(mode);
  }, []);

  const handleRoomCategoryChange = useCallback((category: string) => {
    setSelectedRoomCategory(category);
  }, []);

  const handleSpecificRoomSelect = useCallback((room: any) => {
    setSelectedSpecificRoom(room);
  }, []);
  
  const toggleCategory = useCallback((categoryType: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryType)) {
        next.delete(categoryType);
      } else {
        next.add(categoryType);
      }
      return next;
    });
  }, []);

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => {
        // Клик по фону / ESC → закрываем и уничтожаем
        if (!open) onClose();
      }}
    >
      <DialogContent className={cn(
        "h-[100vh] overflow-y-auto rounded-none",
        viewMode === 'compact' ? "max-w-md" : "max-w-6xl"
      )}>
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Быстрое бронирование
            {loading && (
              <Loader2 className="h-4 w-4 animate-spin ml-2" />
            )}
          </DialogTitle>
          <DialogDescription>
            {loading 
              ? "Создаем бронирование через Selectel..."
              : viewMode === 'compact'
                ? "Подтвердите данные гостя и забронируйте выбранный номер"
                : "Заполните данные гостя и выберите номер для создания бронирования"
            }
          </DialogDescription>
        </DialogHeader>

        <div className={cn(
          "grid gap-3",
          viewMode === 'compact' ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-3"
        )}>
          {/* Form section */}
          <div className={viewMode === 'compact' ? "" : "lg:col-span-1"}>
            <div className="space-y-2">
            <Card>
              <CardHeader className="pb-1">
                <CardTitle className="text-base font-semibold">Параметры поиска</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {/* Dates - Two separate calendars */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-sm font-medium">Дата заезда</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(checkInDate, "dd MMM yyyy", { locale: ru })}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={checkInDate}
                          defaultMonth={checkInDate}
                          onSelect={(date) => {
                            if (date) {
                              setCheckInDate(date);
                              setUserChangedDates(true);
                              // Автоматически обновляем дату выезда если она <= новой дате заезда
                              if (checkOutDate <= date) {
                                setCheckOutDate(addDays(date, 1));
                              }
                            }
                          }}
                          disabled={(date) => date < new Date()}
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium">Дата выезда</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(checkOutDate, "dd MMM yyyy", { locale: ru })}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={checkOutDate}
                          defaultMonth={checkOutDate}
                          onSelect={(date) => {
                            if (date && date > checkInDate) {
                              setCheckOutDate(date);
                              setUserChangedDates(true);
                            }
                          }}
                          disabled={(date) => date <= checkInDate}
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                  
                {/* Time inputs */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-sm font-medium">Время заезда</Label>
                    <Input
                      type="time"
                      value={checkInTime}
                      onChange={(e) => setCheckInTime(e.target.value)}
                      className=""
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Время выезда</Label>
                    <Input
                      type="time"
                      value={checkOutTime}
                      onChange={(e) => setCheckOutTime(e.target.value)}
                      className=""
                    />
                  </div>
                </div>

                {/* Guests */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-sm font-medium">Взрослые</Label>
                    <Select value={adults.toString()} onValueChange={(value) => setAdults(Number(value))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5,6,7,8].map(num => (
                          <SelectItem key={num} value={num.toString()}>{num}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium">Дети</Label>
                    <Select value={children.toString()} onValueChange={(value) => setChildren(Number(value))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[0,1,2,3,4].map(num => (
                          <SelectItem key={num} value={num.toString()}>{num}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="pt-1 border-t">
                  <div className="text-sm text-muted-foreground">
                    {nights} ночей • {adults + children} гостей
                  </div>
                  <div className="text-lg font-bold text-primary">
                    {roomsLoading ? 'Загружаем номера...' : `Найдено ${availableRooms.length} номеров`}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Booking Summary - показываем в обоих режимах */}
            {(() => {
              // 🎯 КРИТИЧНО: При клике на ячейку сначала ищем категорию, потом номер
              // Приоритет: 1) Из selectedRoom.room_category_id 2) Из selectedRoomCategory (стейт) 3) Из selectedRoom.room_type
              
              // Определяем номер первым - он может содержать room_category_id
              // 🎯 КРИТИЧНО: Если isAutoSelectMode=true, то actualRoom = null (авто-выбор при сохранении)
              // Иначе используем selectedSpecificRoom
              const actualRoom = isAutoSelectMode ? null : selectedSpecificRoom;
              
              // Теперь определяем категорию - приоритет из номера
              let selectedCategoryData = null;
              
              // Источник 1: ID категории из выбранного номера (самый надежный при клике на ячейку)
              if (actualRoom?.room_category_id) {
                selectedCategoryData = dbCategories.find(c => c.id === actualRoom.room_category_id);
              }
              
              // Источник 2: По имени из стейта (если пользователь вручную выбрал категорию)
              if (!selectedCategoryData && selectedRoomCategory) {
                selectedCategoryData = dbCategories.find(c => c.name === selectedRoomCategory);
              }
              
              // Источник 3: По room_type из selectedRoom
              if (!selectedCategoryData && selectedRoom?.room_type) {
                selectedCategoryData = dbCategories.find(c => 
                  c.name.toLowerCase() === selectedRoom.room_type.toLowerCase()
                );
              }
              
              // Получаем все номера выбранной категории
              const categoryRoomsForSelect = selectedCategoryData 
                ? rooms.filter(r => r.room_category_id === selectedCategoryData!.id)
                : [];
              
              // Проверяем доступность каждого номера
              const roomsAvailabilityMap: Record<string, boolean> = {};
              categoryRoomsForSelect.forEach(room => {
                roomsAvailabilityMap[room.id] = isRoomAvailable(room, checkInDate, checkOutDate, false);
              });
              
              // 🎯 ЕДИНЫЙ ИСТОЧНИК ИСТИНЫ: dailyRates
              // Если dailyRates загружены — используем их сумму
              // Это гарантирует 100% совпадение "Итого" с "Тарификацией по дням"
              let totalPrice = 0;
              let pricePerNight = 0;

              if (dailyRates.length > 0) {
                // Единственный источник истины — реальные цены с сервера
                totalPrice = dailyRates.reduce((sum, r) => sum + r.price, 0);
                pricePerNight = totalPrice / dailyRates.length;
              }
              // Если dailyRates пуст — показываем спиннер (totalPrice = 0)
              
              // 🛡️ Helper: проверка на placeholder-значения
              const isPlaceholderName = (val: string): boolean => {
                const trimmed = val.trim().toLowerCase();
                const placeholders = [
                  'загрузка...', 'loading...', 'загрузка', 'loading',
                  'гость', 'guest', 'гость (данные не найдены)', 
                  'гость (ошибка загрузки)', 'undefined', 'null'
                ];
                return placeholders.includes(trimmed);
              };
              
              // Проверка валидации: телефон, фамилия и имя обязательны + НЕ placeholder
              const isPhoneValid = guestPhone.length === 11 && guestPhone.startsWith('7');
              const isFirstNameValid = guestFirstName.trim() !== '' && !isPlaceholderName(guestFirstName);
              const isLastNameValid = guestLastName.trim() !== '' && !isPlaceholderName(guestLastName);
              const isValid = isFirstNameValid && isLastNameValid && isPhoneValid;
              const validationMessage = !isPhoneValid
                ? 'Заполните Телефон (формат: 79999999999)'
                : !isLastNameValid && !isFirstNameValid 
                  ? 'Заполните Фамилию и Имя (не placeholder!)'
                  : !isLastNameValid
                    ? 'Заполните Фамилию'
                    : !isFirstNameValid
                      ? 'Заполните Имя'
                      : '';
              
              return (
                <Card className="bg-primary/5 border-primary">
                    <CardHeader className="pb-1">
                    <CardTitle className="text-base font-semibold">Стоимость проживания</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1">
                    {/* Выбор категории */}
                    <div>
                      <Label className="text-sm font-medium">Категория номера</Label>
                      <Select 
                        value={selectedCategoryData?.id || ''} 
                        onValueChange={(categoryId) => {
                          const cat = dbCategories.find(c => c.id === categoryId);
                          if (cat) {
                            setSelectedRoomCategory(cat.name);
                            setSelectedCategoryId(categoryId); // 🎯 Устанавливаем ID для надёжного триггера useEffect
                            setSelectedSpecificRoom(null); // Сбрасываем номер при смене категории
                            setIsAutoSelectMode(true); // 🎯 Возвращаем авто-режим при смене категории
                            setDailyRates([]); // 🧹 Очистка старых тарифов при смене категории
                          }
                        }}
                      >
                        <SelectTrigger className="">
                          <SelectValue placeholder="Выберите категорию" />
                        </SelectTrigger>
                        <SelectContent>
                          {categoriesLoading ? (
                            <SelectItem value="loading" disabled>⏳ Загрузка...</SelectItem>
                          ) : dbCategories.length === 0 ? (
                            <SelectItem value="none" disabled>Нет категорий</SelectItem>
                          ) : (
                            dbCategories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>
                                {cat.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Выбор конкретного номера */}
                    <div>
                      <Label className="text-sm font-medium">Конкретный номер</Label>
                      <Select 
                        value={isAutoSelectMode ? 'auto' : (actualRoom?.id || 'auto')} 
                        onValueChange={(roomId) => {
                          if (roomId === 'auto') {
                            setSelectedSpecificRoom(null);
                            setIsAutoSelectMode(true); // 🎯 Включаем авто-режим
                          } else {
                            const room = rooms.find(r => r.id === roomId);
                            if (room) {
                              setSelectedSpecificRoom(room);
                              setIsAutoSelectMode(false); // 🎯 Выключаем авто-режим
                            }
                          }
                        }}
                        disabled={!selectedCategoryData}
                      >
                        <SelectTrigger className="">
                          <SelectValue>
                            {!selectedCategoryData 
                              ? "Сначала выберите категорию"
                              : actualRoom 
                                ? `№${actualRoom.room_number} - ${actualRoom.name || actualRoom.room_type}`
                                : "Авто-выбор"
                            }
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {roomsLoading ? (
                            <SelectItem value="loading" disabled>⏳ Загрузка...</SelectItem>
                          ) : categoryRoomsForSelect.length === 0 ? (
                            <SelectItem value="none" disabled>
                              Нет номеров в категории
                            </SelectItem>
                          ) : (
                            <>
                              <SelectItem value="auto">
                                🎯 Авто-выбор (оптимальный)
                              </SelectItem>
                              {categoryRoomsForSelect.map((room) => {
                                const row = availabilityByRoom[room.id];
                                let statusIcon = '⏳';
                                let statusText = 'Проверяем…';
                                if (availabilityInvalidRange) {
                                  statusIcon = '📅';
                                  statusText = 'Укажите корректные даты';
                                } else if (availabilityFailed) {
                                  statusIcon = '⚠️';
                                  statusText = 'Проверка недоступна';
                                } else if (row && !isCheckingAvailability) {
                                  if (row.is_available) {
                                    statusIcon = '✅';
                                    statusText = 'Свободен';
                                  } else {
                                    statusIcon = '🔒';
                                    statusText = conflictText(row) || 'Занят';
                                  }
                                }
                                const blocked = !row || !row.is_available || isCheckingAvailability;
                                return (
                                  <SelectItem 
                                    key={room.id} 
                                    value={room.id}
                                    disabled={blocked}
                                  >
                                    {statusIcon} №{room.room_number} - {room.name || room.room_type} ({statusText})
                                  </SelectItem>
                                );
                              })}
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Информация о стоимости */}
                    <div className="space-y-1 pt-1 border-t">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-muted-foreground">Ночей:</span>
                        <span className="font-semibold">{nights}</span>
                      </div>
                      <div className="pt-1 border-t">
                        <div className="flex items-center justify-between">
                          <span className="font-bold">Итого:</span>
                          <span className="text-2xl font-bold text-primary">
                            {isLoadingDailyRates ? (
                              <Loader2 className="h-5 w-5 animate-spin inline" />
                            ) : totalPrice > 0 ? (
                              `₽${Math.round(totalPrice).toLocaleString()}`
                            ) : '—'}
                          </span>
                        </div>
                      </div>
                      
                      {/* 📊 Тарификация по дням - раскрывающийся список */}
                      <Collapsible open={showDailyRates} onOpenChange={setShowDailyRates}>
                        <CollapsibleTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="w-full justify-between text-muted-foreground hover:text-foreground"
                          >
                            <span className="flex items-center gap-2">
                              <CalendarIcon className="h-4 w-4" />
                              Тарификация по дням
                            </span>
                            <span className="flex items-center gap-1">
                              {isLoadingDailyRates ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <span className="text-xs">{nights} ноч.</span>
                                  {showDailyRates ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </>
                              )}
                            </span>
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="mt-2 space-y-2">
                            {isLoadingDailyRates ? (
                              <div className="flex items-center justify-center py-4">
                                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                <span className="ml-2 text-sm text-muted-foreground">Загрузка тарифов...</span>
                              </div>
                            ) : dailyRates.length === 0 ? (
                              <div className="text-center py-4 text-sm text-muted-foreground">
                                Нет данных о тарифах
                              </div>
                            ) : (
                              <>
                                {/* Сводка - только Ночей и Сумма */}
                                <div className="grid grid-cols-2 gap-2 text-center text-xs">
                                  <div className="p-2 bg-muted rounded">
                                    <div className="font-bold">{nights}</div>
                                    <div className="text-muted-foreground">Ночей</div>
                                  </div>
                                  <div className="p-2 bg-muted rounded">
                                    <div className="font-bold">
                                      {dailyRates.reduce((sum, r) => sum + r.price, 0).toLocaleString()} ₽
                                    </div>
                                    <div className="text-muted-foreground">Сумма</div>
                                  </div>
                                </div>
                                
                                {/* Список дат */}
                                <div className="max-h-48 overflow-y-auto border rounded text-xs">
                                  {dailyRates.map((rate) => (
                                    <div 
                                      key={rate.date} 
                                      className="flex items-center justify-between px-2 py-1.5 border-b last:border-b-0 hover:bg-muted/50"
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium">
                                          {new Date(rate.date).toLocaleDateString('ru-RU', { 
                                            weekday: 'short', 
                                            day: 'numeric', 
                                            month: 'short' 
                                          })}
                                        </span>
                                        <Badge variant="secondary" className="text-[10px] px-1 py-0">
                                          {rate.seasonName || 'Базовый'}
                                        </Badge>
                                      </div>
                                      <span className="font-semibold">
                                        {rate.price > 0 
                                          ? `${rate.price.toLocaleString()} ₽`
                                          : <span className="text-destructive">—</span>
                                        }
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                    
                    {/* Подсказка валидации */}
                    {!isValid && (
                      <div className="flex items-center gap-2 p-2 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm">
                        <AlertCircle className="h-4 w-4 flex-shrink-0" />
                        <span>{validationMessage}</span>
                      </div>
                    )}
                    
                    <Button
                      onClick={() => handleBooking(actualRoom)}
                      disabled={loading || !isValid || !selectedCategoryData}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-bold text-lg shadow-lg border-2 border-green-700"
                      size="lg"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Сохраняем...
                        </>
                      ) : (
                        '✓ ЗАБРОНИРОВАТЬ'
                      )}
                    </Button>
                    
                    {/* 🔒 Кнопка блокировки номера */}
                    {!isBlockMode ? (
                      <Button
                        variant="outline"
                        onClick={() => setIsBlockMode(true)}
                        disabled={!selectedCategoryData}
                        className="w-full text-gray-700 border-gray-400"
                        size="sm"
                      >
                        <Lock className="mr-2 h-4 w-4" />
                        Заблокировать номер
                      </Button>
                    ) : (
                      <div className="space-y-2 p-3 border-2 border-gray-400 rounded-lg bg-gray-50 dark:bg-gray-900">
                        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                          <Lock className="h-4 w-4" />
                          Блокировка номера
                        </div>
                        <Select value={blockReason} onValueChange={setBlockReason}>
                          <SelectTrigger>
                            <SelectValue placeholder="Причина блокировки..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Ремонт">🔧 Ремонт</SelectItem>
                            <SelectItem value="Уборка">🧹 Уборка</SelectItem>
                            <SelectItem value="VIP-резерв">⭐ VIP-резерв</SelectItem>
                            <SelectItem value="Санобработка">🧴 Санобработка</SelectItem>
                            <SelectItem value="Техническая неисправность">⚠️ Техническая неисправность</SelectItem>
                            <SelectItem value="Другое">📝 Другое</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="flex gap-2">
                          <Button
                            onClick={handleBlock}
                            disabled={loading || !blockReason || !selectedCategoryData}
                            className="flex-1 bg-gray-600 hover:bg-gray-700 text-white"
                            size="sm"
                          >
                            <Lock className="mr-1 h-3 w-3" />
                            Заблокировать
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => { setIsBlockMode(false); setBlockReason(''); }}
                            size="sm"
                          >
                            Отмена
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })()}

            {/* Guest form */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold">Данные гостя</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {/* 🎯 Порядок: Телефон* → Фамилия* → Имя* → Отчество */}
                <div>
                  <Label className="text-sm font-medium">
                    Телефон * (79999999999)
                  </Label>
                  <Input
                    value={guestPhone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 11) {
                        setGuestPhone(value);
                        setPhoneError('');
                      }
                    }}
                    onBlur={(e) => {
                      const phone = e.target.value.replace(/\D/g, '');
                      if (phone && phone.length === 11 && phone.startsWith('7')) {
                        setPhoneError('');
                      } else if (phone && (phone.length !== 11 || !phone.startsWith('7'))) {
                        setPhoneError('Формат: 79999999999');
                      }
                    }}
                    placeholder="79999999999"
                    maxLength={11}
                    className={cn("placeholder:text-muted-foreground/40", phoneError && "border-destructive")}
                  />
                  {phoneError && (
                    <p className="text-xs text-destructive mt-1">{phoneError}</p>
                  )}
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Фамилия *</Label>
                  <Input
                    value={guestLastName}
                    onChange={(e) => setGuestLastName(e.target.value)}
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Имя *</Label>
                  <Input
                    value={guestFirstName}
                    onChange={(e) => setGuestFirstName(e.target.value)}
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Отчество</Label>
                  <Input
                    value={guestMiddleName}
                    onChange={(e) => setGuestMiddleName(e.target.value)}
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Email</Label>
                  <Input
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Комментарий</Label>
                  <Input
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Примечание к бронированию..."
                    className="placeholder:text-muted-foreground/40"
                  />
                </div>
              </CardContent>
            </Card>
            </div>
            
            {/* Кнопка переключения режимов */}
            {viewMode === 'compact' ? (
              <Button 
                variant="link" 
                className="w-full text-primary"
                onClick={() => setViewMode('full')}
              >
                Расширенный режим →
              </Button>
            ) : (
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => setViewMode('compact')}
              >
                ← Компактный режим
              </Button>
            )}
          </div>

          {/* Room selection section - только в широком режиме */}
          {viewMode === 'full' && (
          <div className="lg:col-span-2">
            <div className="space-y-4">
              {/* Selection mode toggle */}
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Выбор номера</h3>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{availableRooms.length} доступно</Badge>
                  <div className="flex rounded-lg border">
                    <Button
                      variant={selectionMode === 'category' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => handleSelectionModeChange('category')}
                      className="rounded-r-none"
                    >
                      По категориям
                    </Button>
                    <Button
                      variant={selectionMode === 'specific' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => handleSelectionModeChange('specific')}
                      className="rounded-l-none"
                    >
                      Конкретный номер
                    </Button>
                  </div>
                </div>
              </div>

              {/* Loading or Empty State */}
              {roomsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : availableRooms.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                    <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Номера не найдены</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Попробуйте изменить даты или количество гостей
                    </p>
                    <Button 
                      variant="outline" 
                      onClick={() => setShowAllRooms(true)}
                      className="mt-2"
                    >
                      Показать все номера
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Category selection mode */}
                  {selectionMode === 'category' && (
                    <div className="space-y-4">
                      {roomCategories.map((category: any) => (
                        <RoomCategoryCard
                          key={category.type}
                          category={category}
                          loading={loading}
                          onBook={() => {
                            // При клике на категорию — подставляем в блок "Стоимость проживания"
                            setSelectedRoomCategory(category.type);
                            setSelectedSpecificRoom(null);
                          }}
                        />
                      ))}
                    </div>
                  )}

                  {/* Specific room selection mode */}
                  {selectionMode === 'specific' && (
                    <div className="space-y-3">
                      {/* Room categories as collapsible sections */}
                      {roomCategories.map((category: any) => {
                        const isExpanded = expandedCategories.has(category.type);
                        
                        return (
                          <Collapsible
                            key={category.type}
                            open={isExpanded}
                            onOpenChange={() => toggleCategory(category.type)}
                          >
                            <Card className={cn(
                              "transition-all",
                              category.isSelectedCategory && "border-primary ring-1 ring-primary"
                            )}>
                              <CollapsibleTrigger asChild>
                                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors p-4">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <ChevronDown 
                                        className={cn(
                                          "h-5 w-5 transition-transform text-muted-foreground",
                                          isExpanded && "transform rotate-180"
                                        )} 
                                      />
                                      <div>
                                        <h4 className="font-semibold text-base">
                                          {category.type}
                                          {category.isSelectedCategory && (
                                            <Badge variant="default" className="ml-2 text-xs">
                                              Выбранная категория
                                            </Badge>
                                          )}
                                        </h4>
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                          от ₽{category.minPrice.toLocaleString()} за ночь
                                        </p>
                                      </div>
                                    </div>
                                    <Badge variant="secondary" className="ml-auto">
                                      {category.availableCount} {category.availableCount === 1 ? 'номер' : category.availableCount < 5 ? 'номера' : 'номеров'}
                                    </Badge>
                                  </div>
                                </CardHeader>
                              </CollapsibleTrigger>
                              
                              <CollapsibleContent>
                                <CardContent className="pt-0 pb-4 px-4">
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {category.rooms.map((room: any) => {
                                      // 🎯 КРИТИЧНО: Получаем цены из room_rates
                                      const pricing = getRoomPricing(room);
                                      return (
                                        <SpecificRoomCard
                                          key={room.id}
                                          room={room}
                                          nights={nights}
                                          pricePerNight={pricing.pricePerNight}
                                          totalPrice={pricing.totalPrice}
                                          isSelected={selectedSpecificRoom?.id === room.id}
                                          loading={loading}
                                          onSelect={() => handleSpecificRoomSelect(room)}
                                          onBook={() => handleSpecificRoomSelect(room)}
                                        />
                                      );
                                    })}
                                  </div>
                                </CardContent>
                              </CollapsibleContent>
                            </Card>
                          </Collapsible>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
            
          </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
